import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, ArrowLeft, Loader2, Save, GripVertical, UploadCloud, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { fetchPackages, createPackage, updatePackage, deletePackage, uploadImage, adminMe, clearToken, getToken } from "../lib/api";
import { COMPANY } from "../data/site";

const EMPTY = {
  title: "", subtitle: "", slug: "", region: "", type: "domestic",
  price: 0, nights: 0, days: 0, tag: "", image: "", summary: "",
  highlights: [], itinerary: [], inclusions: [], exclusions: [], featured: true,
};

const linesToArr = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

export default function AdminTours() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // package object or EMPTY (new) or null (closed)
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await fetchPackages());
    } catch {
      toast.error("Failed to load tours.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    adminMe().then(load).catch(() => { clearToken(); navigate("/admin/login"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => setEditing({ ...EMPTY, itinerary: [{ day: 1, title: "", desc: "" }] });
  const openEdit = (p) => setEditing({ ...p });

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      nights: Number(editing.nights) || 0,
      days: Number(editing.days) || 0,
    };
    try {
      if (editing.id) await updatePackage(editing.id, payload);
      else await createPackage(payload);
      toast.success(editing.id ? "Tour updated." : "Tour added.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await deletePackage(p.id);
      toast.success("Tour deleted.");
      setRows((r) => r.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => { clearToken(); navigate("/admin/login"); };
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB."); return; }
    setUploading(true);
    try {
      const { data } = await uploadImage(file);
      set("image", data.url);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") && f.size <= 8 * 1024 * 1024);
    if (!list.length) return;
    setGalleryUploading(true);
    try {
      for (const f of list) {
        const { data } = await uploadImage(f);
        setEditing((e) => ({ ...e, images: [...(e.images || []), data.url] }));
      }
      toast.success("Gallery updated.");
    } catch (e) {
      toast.error("Some images failed to upload.");
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryImage = (idx) => setEditing((e) => ({ ...e, images: (e.images || []).filter((_, j) => j !== idx) }));

  const moveGalleryImage = (idx, dir) => setEditing((e) => {
    const imgs = [...(e.images || [])];
    const to = idx + dir;
    if (to < 0 || to >= imgs.length) return e;
    [imgs[idx], imgs[to]] = [imgs[to], imgs[idx]];
    return { ...e, images: imgs };
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#0A221C] text-white">
        <div className="ll-container py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-lg px-3 py-2"><img src={COMPANY.logo} alt="Life Line" className="h-8 w-auto object-contain" /></span>
            <span className="font-display text-xl font-bold hidden sm:block">Tour Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-nav-enquiries" className="text-sm text-white/80 hover:text-white flex items-center gap-2"><ArrowLeft size={16} /> Enquiries</Link>
            <Link to="/admin/blogs" data-testid="admin-nav-blogs" className="text-sm text-white/80 hover:text-white flex items-center gap-2"><Newspaper size={16} /> Blog Manager</Link>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="ll-container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold text-[#0A221C]">Tour Packages</h1>
            <p className="text-[#4A5D58] mt-1">{rows.length} tours live on your website</p>
          </div>
          <button data-testid="add-tour-btn" onClick={openNew} className="btn-primary"><Plus size={18} /> Add New Tour</button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tours-list">
            {rows.map((p) => (
              <div key={p.id} data-testid={`tour-item-${p.slug}`} className="ll-card overflow-hidden">
                <div className="h-40 overflow-hidden bg-black/5">
                  {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-[#4A5D58] mb-1">
                    <span className="capitalize px-2 py-0.5 rounded-full bg-black/5">{p.type}</span>
                    <span>{p.region}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#0A221C] leading-tight">{p.title}</h3>
                  <p className="text-[#4A5D58] text-sm mt-1">₹{Number(p.price).toLocaleString("en-IN")} · {p.nights}N/{p.days}D</p>
                  <div className="flex gap-2 mt-4">
                    <button data-testid={`edit-tour-${p.slug}`} onClick={() => openEdit(p)} className="flex-1 flex items-center justify-center gap-1.5 border border-black/15 rounded-full py-2 text-sm font-medium hover:bg-[#0A221C] hover:text-white transition-colors"><Pencil size={14} /> Edit</button>
                    <button data-testid={`delete-tour-${p.slug}`} onClick={() => remove(p)} className="flex items-center justify-center gap-1.5 border border-red-200 text-red-600 rounded-full py-2 px-3 text-sm font-medium hover:bg-red-600 hover:text-white transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setEditing(null)} />
          <div className="relative bg-[#FDFBF7] w-full max-w-2xl h-full overflow-y-auto shadow-2xl" data-testid="tour-editor">
            <div className="sticky top-0 bg-[#0A221C] text-white px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-display text-xl font-bold">{editing.id ? "Edit Tour" : "Add New Tour"}</h2>
              <button onClick={() => !saving && setEditing(null)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Title *"><input data-testid="tour-title" className="editor-input" value={editing.title} onChange={(e) => set("title", e.target.value)} /></Field>
                <Field label="Subtitle"><input className="editor-input" value={editing.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Type"><select data-testid="tour-type" className="editor-input" value={editing.type} onChange={(e) => set("type", e.target.value)}><option value="domestic">Domestic</option><option value="international">International</option></select></Field>
                <Field label="Region"><input className="editor-input" value={editing.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. Himalaya" /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Price (₹)"><input data-testid="tour-price" type="number" className="editor-input" value={editing.price} onChange={(e) => set("price", e.target.value)} /></Field>
                <Field label="Nights"><input type="number" className="editor-input" value={editing.nights} onChange={(e) => set("nights", e.target.value)} /></Field>
                <Field label="Days"><input type="number" className="editor-input" value={editing.days} onChange={(e) => set("days", e.target.value)} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Tag (badge)"><input className="editor-input" value={editing.tag} onChange={(e) => set("tag", e.target.value)} placeholder="e.g. Bestseller" /></Field>
                <Field label="Slug (auto if blank)"><input className="editor-input" value={editing.slug || ""} onChange={(e) => set("slug", e.target.value)} /></Field>
              </div>
              <Field label="Tour Image">
                <div
                  data-testid="tour-image-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                  className="border-2 border-dashed border-black/15 rounded-2xl p-5 text-center hover:border-[#4ef72d] transition-colors bg-white"
                >
                  {editing.image ? (
                    <div className="relative">
                      <img src={editing.image} alt="preview" className="h-40 w-full object-cover rounded-xl" />
                      <button type="button" onClick={() => set("image", "")} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black"><X size={14} /></button>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center gap-2 py-6">
                      {uploading ? <Loader2 className="animate-spin text-[#4ef72d]" size={28} /> : <UploadCloud className="text-[#4A5D58]" size={30} />}
                      <span className="text-sm font-semibold text-[#0A221C]">{uploading ? "Uploading..." : "Drag & drop an image, or click to upload"}</span>
                      <span className="text-xs text-[#4A5D58]">JPG, PNG, WEBP · up to 8MB</span>
                      <input data-testid="tour-image-file" type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                    </label>
                  )}
                </div>
                <input data-testid="tour-image" className="editor-input mt-2" value={editing.image} onChange={(e) => set("image", e.target.value)} placeholder="…or paste an image URL" />
              </Field>

              <Field label="Gallery Images (slideshow on tour page)">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="tour-gallery">
                  {(editing.images || []).map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={img} alt={`gallery ${idx + 1}`} className="h-20 w-full object-cover rounded-lg" />
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-semibold rounded px-1.5 leading-4">{idx + 1}</span>
                      <button type="button" data-testid={`gallery-remove-${idx}`} onClick={() => removeGalleryImage(idx)} className="absolute -top-2 -right-2 bg-black/70 text-white rounded-full p-1 hover:bg-red-600"><X size={12} /></button>
                      <div className="absolute inset-x-0 bottom-0 flex justify-between px-1 pb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button type="button" data-testid={`gallery-move-left-${idx}`} onClick={() => moveGalleryImage(idx, -1)} disabled={idx === 0} aria-label="Move image left" className="bg-black/70 text-white rounded-full p-1 hover:bg-[#0A221C] disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft size={13} /></button>
                        <button type="button" data-testid={`gallery-move-right-${idx}`} onClick={() => moveGalleryImage(idx, 1)} disabled={idx === (editing.images || []).length - 1} aria-label="Move image right" className="bg-black/70 text-white rounded-full p-1 hover:bg-[#0A221C] disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight size={13} /></button>
                      </div>
                    </div>
                  ))}
                  <label className="h-20 border-2 border-dashed border-black/15 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#4ef72d] transition-colors text-[#4A5D58]">
                    {galleryUploading ? <Loader2 className="animate-spin" size={20} /> : <><Plus size={18} /><span className="text-[10px] mt-1">Add photos</span></>}
                    <input data-testid="tour-gallery-file" type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleGalleryFiles(e.target.files)} />
                  </label>
                </div>
                <p className="text-xs text-[#4A5D58] mt-2">Upload multiple photos — they appear as a slideshow on the tour&apos;s page. Hover a photo to reorder (◀ ▶) or remove it. The order here is the slideshow order.</p>
              </Field>
              <Field label="Summary"><textarea rows={3} className="editor-input resize-none" value={editing.summary} onChange={(e) => set("summary", e.target.value)} /></Field>
              <Field label="Highlights (one per line)"><textarea rows={3} className="editor-input resize-none" value={arrToLines(editing.highlights)} onChange={(e) => set("highlights", linesToArr(e.target.value))} /></Field>

              {/* Itinerary */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="eyebrow text-[#4A5D58]">Day-by-Day Itinerary</label>
                  <button type="button" onClick={() => set("itinerary", [...(editing.itinerary || []), { day: (editing.itinerary?.length || 0) + 1, title: "", desc: "" }])} className="text-sm font-semibold text-[#0A221C] flex items-center gap-1"><Plus size={14} /> Add Day</button>
                </div>
                <div className="space-y-3">
                  {(editing.itinerary || []).map((d, i) => (
                    <div key={i} className="bg-white rounded-xl p-3 border border-black/5">
                      <div className="flex items-center gap-2 mb-2">
                        <GripVertical size={16} className="text-black/20" />
                        <input type="number" className="editor-input !py-1.5 w-16" value={d.day} onChange={(e) => { const it = [...editing.itinerary]; it[i] = { ...d, day: Number(e.target.value) }; set("itinerary", it); }} />
                        <input className="editor-input !py-1.5 flex-1" placeholder="Day title" value={d.title} onChange={(e) => { const it = [...editing.itinerary]; it[i] = { ...d, title: e.target.value }; set("itinerary", it); }} />
                        <button type="button" onClick={() => set("itinerary", editing.itinerary.filter((_, j) => j !== i))} className="text-red-500 p-1"><Trash2 size={15} /></button>
                      </div>
                      <textarea rows={2} className="editor-input !py-1.5 resize-none" placeholder="What happens this day..." value={d.desc} onChange={(e) => { const it = [...editing.itinerary]; it[i] = { ...d, desc: e.target.value }; set("itinerary", it); }} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Inclusions (one per line)"><textarea rows={5} className="editor-input resize-none" value={arrToLines(editing.inclusions)} onChange={(e) => set("inclusions", linesToArr(e.target.value))} /></Field>
                <Field label="Exclusions (one per line)"><textarea rows={5} className="editor-input resize-none" value={arrToLines(editing.exclusions)} onChange={(e) => set("exclusions", linesToArr(e.target.value))} /></Field>
              </div>

              <label className="flex items-center gap-2 text-sm font-medium text-[#0A221C]">
                <input type="checkbox" checked={editing.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#4ef72d]" /> Show in featured sections
              </label>
            </div>

            <div className="sticky bottom-0 bg-[#FDFBF7] border-t border-black/5 px-6 py-4 flex gap-3">
              <button onClick={() => !saving && setEditing(null)} className="btn-outline flex-1">Cancel</button>
              <button data-testid="tour-save" onClick={save} disabled={saving} className="btn-primary flex-1 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} {saving ? "Saving..." : "Save Tour"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="eyebrow text-[#4A5D58] block mb-2">{label}</label>
    {children}
  </div>
);
